import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, FileText, Files, Lock, LockOpen, Unlock } from 'lucide-react';

import { api } from '../lib/api.ts';
import { data as formatarData, percentual, reais } from '../lib/formato.ts';
import { baixar, csvDoFechamento } from '../lib/exportar.ts';
import type { DetalheFechamento, LinhaResultado } from '../lib/tipos.ts';
import { useAuth } from '../contexto/auth.tsx';
import { Aviso, Botao, Campo, Carregando, Cartao, Etiqueta } from '../componentes/ui.tsx';
import { TabelaResultado } from '../componentes/TabelaResultado.tsx';
import {
  Comprovante,
  ComprovantesEmLote,
  type ModoComprovante,
} from '../componentes/Comprovante.tsx';
import { RelatorioExecutivo } from '../componentes/RelatorioExecutivo.tsx';

export function FechamentoDetalhe() {
  const { id } = useParams<{ id: string }>();
  // Reabrir desfaz um fechamento imutavel: exige o nivel mais alto.
  const { pode } = useAuth();
  const podeReabrir = pode('fechamento', 'excluir');
  const cache = useQueryClient();
  const navegar = useNavigate();
  const [comprovante, setComprovante] = useState<LinhaResultado | null>(null);
  const [modo, setModo] = useState<ModoComprovante>('detalhado');
  const [lote, setLote] = useState(false);
  const [executivo, setExecutivo] = useState(false);
  const [reabrindo, setReabrindo] = useState(false);
  const [motivo, setMotivo] = useState('');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['fechamento', id],
    queryFn: () => api<DetalheFechamento>(`/fechamentos/${id}`),
    enabled: id !== undefined,
  });

  const reabrir = useMutation({
    mutationFn: () =>
      api<{ ok: true; aviso: string }>(`/fechamentos/${id}/reabrir`, {
        metodo: 'POST',
        corpo: { motivo },
      }),
    onSuccess: () => {
      setReabrindo(false);
      setMotivo('');
      void cache.invalidateQueries({ queryKey: ['fechamento'] });
      void cache.invalidateQueries({ queryKey: ['fechamentos'] });
    },
  });

  if (isLoading) return <Carregando />;
  if (isError) {
    return (
      <Aviso tom="erro" titulo="Não foi possível abrir este fechamento">
        <p>{error.message}</p>
      </Aviso>
    );
  }
  if (data === undefined) return null;

  const f = data.fechamento;
  const fechado = f.status === 'fechado';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 print:hidden">
        <Link
          to="/historico"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="size-4" />
          Voltar ao histórico
        </Link>

        <div className="flex flex-wrap gap-2">
          {fechado && podeReabrir && (
            <Botao tipo="secundario" onClick={() => setReabrindo(true)}>
              <LockOpen className="size-4" />
              Reabrir mês
            </Botao>
          )}
          <Botao tipo="secundario" onClick={() => setExecutivo(true)}>
            <FileText className="size-4" />
            Relatório executivo
          </Botao>
          <Botao tipo="secundario" onClick={() => setLote(true)}>
            <Files className="size-4" />
            Comprovantes de todos
          </Botao>
          <Botao
            tipo="secundario"
            onClick={() =>
              baixar(
                `premiacao-${f.competencia}-v${f.versao}.csv`,
                csvDoFechamento(f.competencia, data.linhas),
              )
            }
          >
            <Download className="size-4" />
            Planilha
          </Botao>
        </div>
      </div>

      <Cartao
        titulo={`Fechamento de ${f.competencia}`}
        descricao={`Versão ${f.versao} · período de ${formatarData(f.periodoInicio)} a ${formatarData(f.periodoFim)}`}
        acoes={
          <Etiqueta tom={fechado ? 'bom' : f.status === 'reaberto' ? 'medio' : 'neutro'}>
            {fechado ? (
              <span className="inline-flex items-center gap-1">
                <Lock className="size-3" /> fechado
              </span>
            ) : (
              <span className="inline-flex items-center gap-1">
                <Unlock className="size-3" /> {f.status}
              </span>
            )}
          </Etiqueta>
        }
      >
        <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
          <Dado rotulo="Total do fechamento" valor={reais(data.totalGeral)} destaque />
          <Dado rotulo="Pessoas" valor={String(data.linhas.length)} />
          <Dado rotulo="Taxa de ocupação" valor={percentual(f.taxaOcupacao)} />
          <Dado rotulo="Regras aplicadas" valor={f.regrasNome ?? '—'} />
          <Dado
            rotulo="Fechado em"
            valor={f.fechadoEm === null ? '—' : formatarData(f.fechadoEm)}
            nota={f.fechadoPor ?? undefined}
          />
          <Dado
            rotulo="Origem da taxa de ocupação"
            valor={f.taxaOcupacaoOrigem ?? '—'}
            largo
          />
        </div>

        {f.status === 'reaberto' && (
          <div className="mt-4">
            <Aviso titulo="Este fechamento foi reaberto">
              <p>
                Reaberto{f.reabertoEm === null ? '' : ` em ${formatarData(f.reabertoEm)}`}
                {f.reabertoPor === null ? '' : ` por ${f.reabertoPor}`}. Motivo:{' '}
                {f.reaberturaMotivo ?? '—'}
              </p>
              <p>
                Os valores abaixo são os que estavam gravados nesta versão. Uma versão mais nova
                pode existir no histórico.
              </p>
            </Aviso>
          </div>
        )}
      </Cartao>

      {reabrindo && (
        <Cartao titulo={`Reabrir a competência ${f.competencia}`}>
          <div className="max-w-2xl space-y-4">
            <Aviso tom="atencao" titulo="O que acontece ao reabrir">
              <p>
                Esta versão <strong>continua guardada</strong> e consultável — nada do que já foi
                gravado se perde. Ela passa a constar como reaberta, com seu nome e o motivo.
              </p>
              <p>
                A competência volta a aceitar cálculo. Quando você fechar de novo, nasce a
                <strong> versão {f.versao + 1}</strong>, e as duas ficam no histórico lado a lado.
              </p>
              <p>
                Se alguém já recebeu com base nesta versão, o novo valor pode ser diferente — a
                diferença é sua para conciliar.
              </p>
            </Aviso>

            <Campo
              rotulo="Por que está reabrindo"
              valor={motivo}
              onChange={setMotivo}
              dica="Pelo menos 10 caracteres. Ex.: taxa de ocupação informada errada, 74,6 em vez de 76,4."
            />

            {reabrir.isError && (
              <Aviso tom="erro">
                <p>{reabrir.error.message}</p>
              </Aviso>
            )}

            <div className="flex gap-3">
              <Botao
                tipo="perigo"
                onClick={() => reabrir.mutate()}
                carregando={reabrir.isPending}
                desabilitado={motivo.trim().length < 10}
              >
                Confirmar reabertura
              </Botao>
              <Botao
                tipo="secundario"
                onClick={() => {
                  setReabrindo(false);
                  setMotivo('');
                }}
              >
                Cancelar
              </Botao>
            </div>
          </div>
        </Cartao>
      )}

      {reabrir.isSuccess && (
        <Aviso tom="info" titulo="Mês reaberto">
          <p>
            A competência {f.competencia} voltou a aceitar cálculo, e esta versão ficou guardada no
            histórico.
          </p>
          <p>
            <button
              onClick={() => navegar('/fechamento')}
              className="font-medium underline hover:no-underline"
            >
              Ir para o fechamento
            </button>{' '}
            para calcular de novo.
          </p>
        </Aviso>
      )}

      <Cartao
        titulo="Resultado por pessoa"
        descricao="Clique no nome para ver o memorial de cálculo, ou no ícone à direita para o comprovante individual."
      >
        <TabelaResultado linhas={data.linhas} onComprovante={setComprovante} />
      </Cartao>

      {comprovante !== null && (
        <Comprovante
          fechamento={f}
          linha={comprovante}
          modo={modo}
          onModo={setModo}
          onFechar={() => setComprovante(null)}
        />
      )}

      {lote && (
        <ComprovantesEmLote
          fechamento={f}
          linhas={data.linhas}
          modo={modo}
          onModo={setModo}
          onFechar={() => setLote(false)}
        />
      )}

      {executivo && (
        <RelatorioExecutivo
          fechamento={f}
          linhas={data.linhas}
          onFechar={() => setExecutivo(false)}
        />
      )}
    </div>
  );
}

function Dado({
  rotulo,
  valor,
  nota,
  destaque = false,
  largo = false,
}: {
  rotulo: string;
  valor: string;
  nota?: string;
  destaque?: boolean;
  largo?: boolean;
}) {
  return (
    <div className={largo ? 'sm:col-span-2' : undefined}>
      <p className="text-xs font-medium tracking-wide text-slate-500 uppercase">{rotulo}</p>
      <p
        className={
          destaque
            ? 'mt-1 font-mono text-2xl font-semibold text-slate-900'
            : 'mt-1 text-sm text-slate-900'
        }
      >
        {valor}
      </p>
      {nota !== undefined && <p className="text-xs text-slate-500">{nota}</p>}
    </div>
  );
}
